/**
 * authBridge.js
 *
 * Cross-origin anonymous-UID unification for the Muuzbox platform.
 *
 * PROBLEM
 * -------
 * Firebase Auth state is stored per-origin (in IndexedDB scoped to
 * `origin`). We have several origins in play — muuzbox.com,
 * admin.muuzbox.com, thisisobah.com — and a guest crossing between
 * them via iframe or direct navigation ends up with a DIFFERENT
 * anonymous UID on each. That fragments their votes, chats, request
 * history, and prize-draw participation across "different users" who
 * are the same person. It also breaks the AnonLinkPrompt threshold —
 * a guest could make 8 requests on one surface and 6 on another and
 * never hit the count-10 threshold on either.
 *
 * SOLUTION
 * --------
 * When a "parent" origin already has an authenticated user (anonymous
 * or otherwise), it broadcasts a signed ID token to any embedded
 * iframes via postMessage. The child iframe forwards the token to a
 * Cloud Function (`bridgeAuthToken`) which verifies it and mints a
 * Firebase Custom Token for the SAME uid. The child then calls
 * `signInWithCustomToken` and now shares the parent's uid — same
 * Firestore documents, same request history, same everything.
 *
 * Notes:
 *   • Origin allowlist enforced on BOTH sides.
 *   • Only fires when child has no local user OR local uid differs
 *     from parent. Idempotent — cheap to call repeatedly.
 *   • Broadcast is send-and-forget; child-side is fire-and-verify.
 *   • Custom-token sign-in flips `auth.currentUser.isAnonymous` to
 *     `false` on the child side. Downstream code that checks
 *     "is this a guest?" must use `users/{uid}.anonymousSince` from
 *     Firestore, not `auth.currentUser.isAnonymous`. See the
 *     accompanying schema field and AnonLinkPrompt update.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../firebase-config';

const MSG_TYPE = 'muuzbox-auth-bridge-v1';

// Only accept postMessages from these origins. Update this list when a
// new Muuzbox surface is added.
const TRUSTED_ORIGINS = new Set([
  'https://muuzbox.com',
  'https://www.muuzbox.com',
  'https://admin.muuzbox.com',
  'https://audio.muuzbox.com',
  'https://player.muuzbox.com',
  'https://oba.muuzbox.com',
  'https://thisisobah.com',
  'https://www.thisisobah.com',
  // Local dev — accept only when a local dev port is explicit
  'http://localhost:3000',
  'http://localhost:3001',
  'http://localhost:5173',
]);

// Callable Cloud Function name. The function is deployed separately;
// see muuzbox-backend/functions/src/bridgeAuthToken.js for the source.
const CLOUD_FN_URL =
  'https://us-central1-chatbout001.cloudfunctions.net/bridgeAuthToken';

const log = (...args) => console.log('[authBridge]', ...args);
const warn = (...args) => console.warn('[authBridge]', ...args);

// ─── Parent-side: broadcast this origin's auth to embedded iframes ──

/**
 * Call this whenever an iframe is added to the DOM (or the current
 * user's auth state changes). Iterates over every `<iframe>` currently
 * mounted and posts our current ID token to each. Silent no-op if
 * there's no authenticated user.
 *
 * The child iframes decide whether to accept — origin allowlist +
 * `signInWithCustomToken` verification prevents forgery.
 */
export async function broadcastAuthToChildren() {
  const user = auth?.currentUser;
  if (!user) return; // nothing to share

  let idToken;
  try {
    idToken = await user.getIdToken(false); // false = don't force refresh
  } catch (err) {
    warn('getIdToken failed:', err?.message);
    return;
  }
  if (!idToken) return;

  const payload = {
    type: MSG_TYPE,
    idToken,
    parentUid: user.uid,
  };

  try {
    const iframes = document.getElementsByTagName('iframe');
    for (let i = 0; i < iframes.length; i++) {
      try {
        // Use "*" for targetOrigin so we can reach cross-domain iframes.
        // Security is enforced by the RECEIVING side's origin check.
        iframes[i].contentWindow?.postMessage(payload, '*');
      } catch (_) { /* iframe not accessible, skip */ }
    }
    log('broadcast auth to', iframes.length, 'iframe(s), uid=', user.uid);
  } catch (err) {
    warn('broadcast failed:', err?.message);
  }
}

/**
 * Convenience: watch for auth state changes and rebroadcast to iframes.
 * Call once at app startup on any origin that hosts iframes.
 */
export function startBroadcasting() {
  // Broadcast on every auth state change.
  try {
    auth?.onAuthStateChanged(() => {
      broadcastAuthToChildren().catch(() => {});
    });
  } catch (err) {
    warn('failed to attach auth listener:', err?.message);
  }

  // Also re-broadcast whenever new iframes appear in the DOM. Uses a
  // MutationObserver so we don't have to hook into every add-iframe
  // call site.
  try {
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        for (const node of r.addedNodes) {
          if (node?.tagName === 'IFRAME') {
            // Iframe added — wait for it to load, then broadcast.
            const send = () => broadcastAuthToChildren().catch(() => {});
            node.addEventListener('load', send, { once: true });
            // Also send immediately in case the iframe is already loaded
            setTimeout(send, 250);
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  } catch (err) {
    warn('MutationObserver setup failed:', err?.message);
  }
}

// ─── Child-side: receive parent's auth and sign in with same uid ────

let _receiverInstalled = false;

/**
 * Call this once at app startup on origins that may be loaded as an
 * iframe or launched from another Muuzbox surface. Listens for the
 * postMessage from the parent, hands off to the Cloud Function to
 * mint a custom token, then signs in as the same UID.
 *
 * Idempotent — installing multiple times is safe (guarded by
 * `_receiverInstalled`). Silent when no message arrives (e.g. direct
 * load, no parent to bridge from).
 */
export function startReceiving() {
  if (_receiverInstalled) return;
  _receiverInstalled = true;

  window.addEventListener('message', async (evt) => {
    // 1. Origin allowlist — reject anything not from a Muuzbox surface.
    if (!TRUSTED_ORIGINS.has(evt.origin)) return;

    const data = evt.data;
    if (!data || data.type !== MSG_TYPE) return;
    if (!data.idToken || !data.parentUid) return;

    // 2. Already signed in as the same UID? No-op.
    const local = auth?.currentUser;
    if (local && local.uid === data.parentUid) {
      log('local uid already matches parent, skipping bridge');
      return;
    }

    log('received parent auth handoff, parentUid=', data.parentUid);

    // 3. Ask the Cloud Function for a custom token for parent's UID.
    //    Deployed as an HTTP `onRequest` function so we POST the body
    //    directly (not wrapped in a `data` envelope like Callable).
    let customToken;
    try {
      const resp = await fetch(CLOUD_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken: data.idToken }),
      });
      if (!resp.ok) {
        warn('bridgeAuthToken responded', resp.status);
        return;
      }
      const body = await resp.json();
      customToken = body?.customToken;
    } catch (err) {
      warn('bridgeAuthToken call failed:', err?.message);
      return;
    }
    if (!customToken) {
      warn('bridgeAuthToken returned no token');
      return;
    }

    // 4. Sign in as the parent's UID.
    try {
      await signInWithCustomToken(auth, customToken);
      log('✓ signed in with parent UID', data.parentUid);
    } catch (err) {
      warn('signInWithCustomToken failed:', err?.message);
    }
  });
}

// ─── Debug helpers (exposed on window in dev) ──────────────────────

if (typeof window !== 'undefined') {
  window.__muuzboxAuthBridge = {
    broadcastAuthToChildren,
    startBroadcasting,
    startReceiving,
    trustedOrigins: [...TRUSTED_ORIGINS],
  };
}
